import React, { useState, useEffect } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTruck,
  faShip,
  faPlane,
  faTrain,
  faSpinner,
  faMapMarkerAlt,
  faCalendarAlt,
  faBox,
  faFileAlt,
  faRoute,
  faCheckCircle,
  faHourglassHalf,
  faExclamationTriangle,
  faClock,
  faWeightHanging,
  faCubes,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";

const TransportTab = ({ item }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [transportData, setTransportData] = useState(null);

  useEffect(() => {
    fetchTransportData();
  }, [item]);

  const fetchTransportData = async () => {
    try {
      setLoading(true);
      setError(null);

      const reference = item?.header?.reference;
      if (!reference) {
        setError("No reference number available");
        setLoading(false);
        return;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_API_URL}/api/transport`,
        { params: { reference } }
      );

      if (response.data.success) {
        setTransportData(response.data.data);
      } else {
        setError("Failed to fetch transport data");
      }
    } catch (err) {
      console.error("Error fetching transport data:", err);
      setError(
        err.response?.data?.message || "Error loading transport data"
      );
    } finally {
      setLoading(false);
    }
  };

  const getModeIcon = (mode) => {
    switch (mode) {
      case "truck":
        return faTruck;
      case "ship":
        return faShip;
      case "air":
        return faPlane;
      case "rail":
        return faTrain;
      default:
        return faRoute;
    }
  };

  const getModeColor = (mode) => {
    switch (mode) {
      case "truck":
        return "text-amber-600";
      case "ship":
        return "text-blue-600";
      case "air":
        return "text-sky-500";
      case "rail":
        return "text-gray-600";
      default:
        return "text-purple-600";
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      booked: { bg: "bg-gray-100", text: "text-gray-700", label: "Booked" },
      picked_up: { bg: "bg-blue-100", text: "text-blue-700", label: "Picked Up" },
      in_transit: { bg: "bg-amber-100", text: "text-amber-700", label: "In Transit" },
      at_customs: { bg: "bg-purple-100", text: "text-purple-700", label: "At Customs" },
      out_for_delivery: { bg: "bg-indigo-100", text: "text-indigo-700", label: "Out for Delivery" },
      delivered: { bg: "bg-green-100", text: "text-green-700", label: "Delivered" },
      delayed: { bg: "bg-red-100", text: "text-red-700", label: "Delayed" },
      cancelled: { bg: "bg-red-100", text: "text-red-700", label: "Cancelled" },
    };

    const config = statusConfig[status] || statusConfig.booked;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <FontAwesomeIcon
            icon={faSpinner}
            className="text-blue-500 text-5xl mb-4 animate-spin"
          />
          <p className="text-gray-600 text-lg">Loading transport data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="text-center">
          <div className="flex items-center justify-center space-x-4 mb-4">
            <FontAwesomeIcon icon={faTruck} className="text-gray-400 text-4xl" />
            <FontAwesomeIcon icon={faShip} className="text-gray-400 text-4xl" />
            <FontAwesomeIcon icon={faPlane} className="text-gray-400 text-4xl" />
          </div>
          <p className="text-gray-700 text-xl font-semibold mb-2">
            Transport Data Not Available
          </p>
          <p className="text-gray-500 mb-4">{error}</p>
          <button
            onClick={fetchTransportData}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!transportData) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="text-center">
          <FontAwesomeIcon
            icon={faRoute}
            className="text-gray-400 text-5xl mb-4"
          />
          <p className="text-gray-700 text-xl font-semibold mb-2">
            No Transport Data
          </p>
          <p className="text-gray-500">
            No transport information found for this shipment.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-full overflow-x-hidden bg-gray-50">
      <div className="space-y-4 p-4">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <FontAwesomeIcon
                icon={getModeIcon(transportData.primaryMode)}
                className={`text-2xl ${getModeColor(transportData.primaryMode)}`}
              />
              <div>
                <h2 className="text-lg font-bold text-gray-900">
                  Transport Details
                </h2>
                <p className="text-sm text-gray-500">
                  Ref: {transportData.reference}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {getStatusBadge(transportData.currentStatus)}
              <button
                onClick={fetchTransportData}
                className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50 transition"
              >
                Refresh
              </button>
            </div>
          </div>

          {/* Route Summary */}
          <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
            <div className="text-center">
              <p className="text-xs text-gray-500 mb-1">Origin</p>
              <p className="font-semibold text-gray-800">
                {transportData.origin?.city}
              </p>
              <p className="text-xs text-gray-500">
                {transportData.origin?.country}
              </p>
            </div>
            <div className="flex-1 px-4">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t-2 border-dashed border-gray-300"></div>
                </div>
                <div className="relative flex justify-center">
                  <FontAwesomeIcon
                    icon={getModeIcon(transportData.primaryMode)}
                    className={`text-xl bg-gray-50 px-2 ${getModeColor(transportData.primaryMode)}`}
                  />
                </div>
              </div>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-500 mb-1">Destination</p>
              <p className="font-semibold text-gray-800">
                {transportData.destination?.city}
              </p>
              <p className="text-xs text-gray-500">
                {transportData.destination?.country}
              </p>
            </div>
          </div>
        </div>

        {/* Info Cards Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {/* Carrier */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
            <div className="flex items-center gap-2 mb-1">
              <FontAwesomeIcon icon={faTruck} className="text-gray-400 text-sm" />
              <p className="text-xs text-gray-500">Carrier</p>
            </div>
            <p className="font-semibold text-gray-800 text-sm">
              {transportData.carrier?.name || "N/A"}
            </p>
            <p className="text-xs text-gray-500">{transportData.carrier?.code}</p>
          </div>

          {/* Weight */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
            <div className="flex items-center gap-2 mb-1">
              <FontAwesomeIcon icon={faWeightHanging} className="text-gray-400 text-sm" />
              <p className="text-xs text-gray-500">Weight</p>
            </div>
            <p className="font-semibold text-gray-800 text-sm">
              {transportData.weight?.value?.toLocaleString() || "N/A"}{" "}
              {transportData.weight?.unit}
            </p>
          </div>

          {/* Volume */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
            <div className="flex items-center gap-2 mb-1">
              <FontAwesomeIcon icon={faCubes} className="text-gray-400 text-sm" />
              <p className="text-xs text-gray-500">Volume</p>
            </div>
            <p className="font-semibold text-gray-800 text-sm">
              {transportData.volume?.value || "N/A"} {transportData.volume?.unit}
            </p>
          </div>

          {/* ETA */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
            <div className="flex items-center gap-2 mb-1">
              <FontAwesomeIcon icon={faCalendarAlt} className="text-gray-400 text-sm" />
              <p className="text-xs text-gray-500">
                {transportData.currentStatus === "delivered" ? "Delivered" : "ETA"}
              </p>
            </div>
            <p className="font-semibold text-gray-800 text-sm">
              {formatDate(
                transportData.actualArrival || transportData.scheduledArrival
              )}
            </p>
          </div>
        </div>

        {/* Current Location */}
        {transportData.currentLocation && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <FontAwesomeIcon icon={faMapMarkerAlt} className="text-red-500" />
              Current Location
            </h3>
            <p className="text-gray-700">
              {transportData.currentLocation.city},{" "}
              {transportData.currentLocation.country}
            </p>
          </div>
        )}

        {/* Route Legs */}
        {transportData.routeLegs && transportData.routeLegs.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <FontAwesomeIcon icon={faRoute} className="text-blue-500" />
              Route Legs
            </h3>
            <div className="space-y-3">
              {transportData.routeLegs.map((leg, index) => (
                <div
                  key={index}
                  className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg"
                >
                  <div className={`p-2 rounded-full ${leg.status === 'completed' ? 'bg-green-100' : 'bg-amber-100'}`}>
                    <FontAwesomeIcon
                      icon={getModeIcon(leg.mode)}
                      className={`text-sm ${leg.status === 'completed' ? 'text-green-600' : 'text-amber-600'}`}
                    />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <p className="font-medium text-gray-800">
                        Leg {leg.legNumber}: {leg.carrier}
                      </p>
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        leg.status === 'completed'
                          ? 'bg-green-100 text-green-700'
                          : leg.status === 'in_transit'
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {leg.status?.replace('_', ' ').toUpperCase()}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">
                      {leg.origin?.city} → {leg.destination?.city}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {leg.vehicleId && `Vehicle: ${leg.vehicleId}`}
                      {leg.driverName && ` | Driver: ${leg.driverName}`}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tracking Events */}
        {transportData.trackingEvents && transportData.trackingEvents.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <FontAwesomeIcon icon={faClock} className="text-purple-500" />
              Tracking Timeline
            </h3>
            <div className="relative">
              <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>
              <div className="space-y-4">
                {transportData.trackingEvents.map((event, index) => (
                  <div key={index} className="relative pl-10">
                    <div className={`absolute left-2.5 w-3 h-3 rounded-full border-2 border-white ${
                      index === 0 ? 'bg-blue-500' : 'bg-gray-300'
                    }`}></div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-1">
                        <p className="font-medium text-gray-800 text-sm">
                          {event.status}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatDate(event.timestamp)}
                        </p>
                      </div>
                      {event.location && (
                        <p className="text-xs text-gray-600 flex items-center gap-1">
                          <FontAwesomeIcon icon={faMapMarkerAlt} className="text-gray-400" />
                          {event.location}
                        </p>
                      )}
                      {event.description && (
                        <p className="text-xs text-gray-500 mt-1">
                          {event.description}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Containers */}
        {transportData.containers && transportData.containers.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <FontAwesomeIcon icon={faBox} className="text-amber-500" />
              Containers
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {transportData.containers.map((container, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-2 bg-gray-50 rounded"
                >
                  <div>
                    <p className="font-medium text-gray-800 text-sm">
                      {container.containerId}
                    </p>
                    <p className="text-xs text-gray-500">{container.containerType}</p>
                  </div>
                  <p className="text-xs text-gray-600">
                    Seal: {container.sealNumber}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Documents */}
        {transportData.documents && transportData.documents.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <FontAwesomeIcon icon={faFileAlt} className="text-green-500" />
              Documents
            </h3>
            <div className="space-y-2">
              {transportData.documents.map((doc, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-2 bg-gray-50 rounded"
                >
                  <div>
                    <p className="font-medium text-gray-800 text-sm">
                      {doc.docType}
                    </p>
                    <p className="text-xs text-gray-500">{doc.number}</p>
                  </div>
                  <span className={`px-2 py-0.5 rounded text-xs ${
                    doc.status === 'Released' || doc.status === 'Cleared' || doc.status === 'Verified'
                      ? 'bg-green-100 text-green-700'
                      : doc.status === 'Processing' || doc.status === 'Pending'
                      ? 'bg-amber-100 text-amber-700'
                      : 'bg-gray-100 text-gray-700'
                  }`}>
                    {doc.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Special Instructions */}
        {transportData.specialInstructions && (
          <div className="bg-amber-50 rounded-lg border border-amber-200 p-4">
            <h3 className="text-sm font-semibold text-amber-800 mb-2 flex items-center gap-2">
              <FontAwesomeIcon icon={faInfoCircle} className="text-amber-500" />
              Special Instructions
            </h3>
            <p className="text-sm text-amber-700">
              {transportData.specialInstructions}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TransportTab;
